import React, { useState, useEffect, useRef } from 'react';
import { Content } from 'ui/views/dialogs/Dialog';
import { CompanyProfile } from 'types/company';
import { invalidate } from 'hooks/useSWR';
import CompanyMetadataInput from './CompanyMetadataInput';
import useSessionStorage from 'hooks/useSessionStorage';
import Button from 'ui/elements/buttons/Button';
import { logError } from 'util/logging';
import ProgressBar from 'ui/elements/Progress/ProgressBar';
import Quotes from './Quotes';
import { companyProfileKey } from 'apis/CompanyAPI/companies/useCompanyProfile';
import useLatestValue from 'hooks/useLatestValue';
import { ImpactGoal } from 'types';
import usePatchCompanyProfile from 'apis/CompanyAPI/companies/usePatchCompanyProfile';
import { useImpactGoals } from 'apis/CompanyAPI/companies/useImpactGoals';
import { useExecutiveSummary } from 'apis/CompanyAPI/companies/useExecutiveSummary';
import { useMarketPotential } from 'apis/CompanyAPI/companies/useMarketPotential';
import Resources from 'util/resource/Resources';

import { useCompanyOnboardingWebsocket, useCompanyOnboardingAPI } from 'hooks/onboarding';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { OnboardingMode } from './CompanyWizard';
import { CompanyOnboardingSuggestions } from 'types/onboarding';

export function useAiOnboardingSessionStorage() {
  const [isAiOnboarding, setIsAiOnboarding] = useSessionStorage('ai-onboarding', false);
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('ai-onboarding');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  return [isAiOnboarding, setIsAiOnboarding] as const;
}

const PROGRESS_PHASES = [
  { percentage: 20, message: 'Gathering company information...' },
  { percentage: 50, message: 'Analyzing company data...' },
  { percentage: 94, message: 'Crafting recommendations...' },
  { percentage: 99, message: 'Almost done...' },
  { percentage: 100, message: 'Complete!' },
];

function useProgressAnimation(targetPercentage: number) {
  const [currentPercentage, setCurrentPercentage] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(PROGRESS_PHASES[0].message);
  const animationRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);

  useEffect(() => {
    const animate = () => {
      const now = Date.now();
      // ~60fps
      if (now - lastUpdateRef.current < 16) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastUpdateRef.current = now;

      setCurrentPercentage(prev => {
        const next = (() => {
          const diff = targetPercentage - prev;
          if (Math.abs(diff) < 0.1) return targetPercentage;

          const speed = targetPercentage === 100 ? 0.15 : 0.015;
          const step = diff * speed;
          return prev + step;
        })();

        // Only allow progress to go forward, or stay the same
        return Math.max(prev, next);
      });

      const phase =
        PROGRESS_PHASES.find(p => currentPercentage < p.percentage) || PROGRESS_PHASES[PROGRESS_PHASES.length - 1];
      setCurrentMessage(phase.message);

      if (currentPercentage < targetPercentage) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetPercentage, currentPercentage]);

  return { percentage: currentPercentage, message: currentMessage };
}

export default function AiOnboarding({
  companyProfile,
  onComplete,
  setOnboardingMode,
}: {
  companyProfile: CompanyProfile;
  onComplete: () => void;
  setOnboardingMode: (value: OnboardingMode) => void;
}) {
  const { resource: impactGoalsResource } = useImpactGoals(companyProfile.id);
  const { resource: executiveSummaryResource } = useExecutiveSummary(companyProfile.id);
  const { resource: marketPotentialResource } = useMarketPotential(companyProfile.id);

  return (
    <Resources
      resources={[impactGoalsResource, executiveSummaryResource, marketPotentialResource]}
      renderLoading="Bar"
      renderError={() => (
        <AiOnboardingCore
          companyProfile={companyProfile}
          onComplete={onComplete}
          setOnboardingMode={setOnboardingMode}
          impactGoals={[]}
          executiveSummary={{}}
          marketPotential={undefined}
        />
      )}
    >
      {([impactGoals, executiveSummary, marketPotential]) => (
        <AiOnboardingCore
          companyProfile={companyProfile}
          onComplete={onComplete}
          setOnboardingMode={setOnboardingMode}
          impactGoals={impactGoals}
          executiveSummary={executiveSummary}
          marketPotential={marketPotential}
        />
      )}
    </Resources>
  );
}

function OnboardingWaitingAnimation({
  suggestions,
  aiProgress,
  onTimeout,
}: {
  aiProgress: AiProgress;
  suggestions: CompanyOnboardingSuggestions;
  onTimeout: () => void;
}) {
  const { isMobile } = useBreakpoints();
  const [targetProgress, setTargetProgress] = useState(5);
  const { percentage: animatedProgress, message: statusMessage } = useProgressAnimation(targetProgress);

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = [];
    if (aiProgress === 'completed') {
      setTargetProgress(100);
      return;
    }

    if (aiProgress === 'fetching-suggestions') {
      const maxTimeout = 45000;

      // Progress milestones at same percentage of max timeout for both modes
      timeouts.push(
        setTimeout(() => setTargetProgress(20), maxTimeout * 0.075),
        setTimeout(() => setTargetProgress(35), maxTimeout * 0.2),
        setTimeout(() => setTargetProgress(50), maxTimeout * 0.375),
        setTimeout(() => setTargetProgress(65), maxTimeout * 0.55),
        setTimeout(() => setTargetProgress(80), maxTimeout * 0.7),
        setTimeout(() => setTargetProgress(90), maxTimeout * 0.875),

        // Force completion at max timeout regardless of API response
        setTimeout(() => {
          setTargetProgress(100);
          onTimeout();
        }, maxTimeout),
      );
    }

    return () => timeouts.forEach(clearTimeout);
  }, [aiProgress, suggestions, onTimeout]);

  return (
    <Content
      style={{ margin: isMobile ? undefined : '10rem 0' }}
      className="u-flex u-flex-align-center u-flex--column u-content-spacing-top"
    >
      <div className="text-body u-content-spacing-bottom" style={{ minHeight: '24px' }}>
        {statusMessage}
      </div>
      <div style={{ width: '100%', maxWidth: '400px', marginBottom: '1rem' }}>
        <ProgressBar color="blue" percentage={animatedProgress} />
      </div>
      <p className="u-half-spacing-top text-metadata">Usually takes 30-40 seconds</p>
      <div className="text-align-center u-section-spacing-top">
        <div className="text-metadata" style={{ fontWeight: '500', fontSize: '18px' }}>
          Did you know?
        </div>
        <Quotes />
      </div>
    </Content>
  );
}

type AiProgress = 'idle' | 'fetching-suggestions' | 'completed' | 'skipped' | 'failed';

function AiOnboardingCore({
  companyProfile,
  onComplete,
  setOnboardingMode,
  impactGoals,
  executiveSummary,
  marketPotential,
}: {
  companyProfile: CompanyProfile;
  onComplete: () => void;
  setOnboardingMode: (mode: OnboardingMode) => void;
  impactGoals: ImpactGoal[];
  executiveSummary: any;
  marketPotential: any;
}) {
  const { isMobile } = useBreakpoints();

  const [_, setAiOnboardingSessionStorage] = useAiOnboardingSessionStorage();

  const [aiProgress, setAiProgress] = React.useState<AiProgress>('idle');

  const latestState = useLatestValue({ aiProgress });

  const { fetchSuggestions } = useCompanyOnboardingAPI(companyProfile.slug);

  const { updateBasedOnSuggestions } = usePatchCompanyProfile(
    companyProfile,
    impactGoals,
    executiveSummary,
    marketPotential,
    [
      'Mission',
      'Location',
      'Employees',
      'EstablishedYear',
      'Website',
      'Branding',
      'Industries',
      'ProductStage',
      'FundingStage',
      'ImpactGoals',
      'TeamDescription',
      'Problem',
      'Solution',
      'WhyInvestInUs',
      'MarketPotential',
    ],
  );

  const { suggestions } = useCompanyOnboardingWebsocket(
    companyProfile.slug,
    suggestions => {
      if (latestState.current.aiProgress === 'fetching-suggestions') {
        setAiProgress('completed');
        updateBasedOnSuggestions(suggestions);

        invalidate(companyProfileKey(companyProfile.slug));
        // Let the user see that the generation is complete before closing the wizard
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    },
    () => {
      if (latestState.current.aiProgress !== 'completed') {
        setAiProgress('failed');
      }
    },
    () => {
      setAiProgress('failed');
    },
  );

  const onTimeout = () => {
    if (aiProgress === 'fetching-suggestions') {
      // If we have suggestions, mark as completed, otherwise show error
      if (Object.keys(suggestions).length > 0) {
        setAiProgress('completed');
        updateBasedOnSuggestions(suggestions);
        invalidate(companyProfileKey(companyProfile.slug));
        onComplete();
      } else {
        // No suggestions received, show error message
        setAiProgress('failed');
        setAiOnboardingSessionStorage(false);
      }
    }
  };

  if (aiProgress === 'idle')
    return (
      <CompanyMetadataInput
        onSubmit={async data => {
          setAiProgress('fetching-suggestions');
          setAiOnboardingSessionStorage(true);

          try {
            await fetchSuggestions({
              location: [data.location.place, data.location.country].filter(v => !!v).join(', '),
              founded: data.founded,
              previousSuggestions: {},
            });
          } catch (e) {
            logError(e, 'Failed to fetch onboarding suggestions');
            setAiProgress('failed');
            setAiOnboardingSessionStorage(false);
            return;
          }
        }}
        onUseManualInput={() => setOnboardingMode('manual input')}
      />
    );

  if (aiProgress === 'failed') {
    return (
      <Content
        style={{ margin: isMobile ? undefined : '10rem 0' }}
        className="u-flex u-flex-align-center u-flex--column u-content-spacing-top"
      >
        <div className="text-body u-content-spacing-bottom" style={{ minHeight: '24px' }}>
          We weren&apos;t able to auto-complete your company profile.
        </div>
        <div style={{ width: '100%', maxWidth: '400px', marginBottom: '1rem' }}>
          <ProgressBar color="blue" percentage={100} />
        </div>
        <div className="u-section-spacing-top text-align-center" style={{ maxWidth: '700px' }}>
          This can happen occasionally, and it&apos;s nothing to worry about. You&apos;ll now be guided through a brief
          manual setup to finish onboarding smoothly.
          <div>
            <Button className="u-half-spacing-top" kind="primary" onClick={() => setOnboardingMode('ai failed')}>
              Continue
            </Button>
          </div>
        </div>
      </Content>
    );
  }

  return <OnboardingWaitingAnimation aiProgress={aiProgress} onTimeout={onTimeout} suggestions={suggestions} />;
}
